import React, { useContext, useLayoutEffect, useRef, useState } from "react";

import { Box, Container, Link, Typography, useTheme } from "@mui/material";

import {
  Email,
  EmailRounded,
  LinkRounded,
  LocationOnRounded,
  Phone,
  PhoneRounded,
} from "@mui/icons-material";

import { Link as RouterLink } from "react-router-dom";

import { CondicaoContext } from "../contexto";
import IdentidadeVisual from "../estilos/identidadeVisual";
import { ajustarTamanhoFonteAdmin } from "../estilos/global";

const textoNovo = {
  color: "#ffffff",
  fontSize: ajustarTamanhoFonteAdmin("0.9rem"),
  fontWeight: 400,
  lineHeight: 1.35,
};

const iconeNovo = {
  color: "#ffffff",
  fontSize: 23,
  flexShrink: 0,
};

const linhaNova = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  minWidth: 0,
};

const separadorNovo = {
  minWidth: 0,
  borderLeft: {
    xs: "none",
    md: "1px solid var(--sear-borda, rgba(255,255,255,0.5))",
  },
  borderTop: {
    xs: "1px solid var(--sear-borda, rgba(255,255,255,0.3))",
    md: "none",
  },
  pt: { xs: 1, md: 0 },
};

const DISTANCIA_MINIMA_RECOLHIMENTO = 120;

// Padding superior/inferior, margem e borda que desaparecem.
const ESPACO_VERTICAL_RECOLHIVEL = 4 + 2 + 4 + 1;

export default function Footer({
  visualNovo = false,
  recolherAoRolar = false,
  posicaoRolagem = 0,
}) {
  const { condicao, role } = useContext(CondicaoContext);
  const tema = useTheme();

  const rodapeAdmin = !condicao && role === "admin";
  const automatico = visualNovo && recolherAoRolar;

  const detalhesRef = useRef(null);
  const [alturaDetalhes, setAlturaDetalhes] = useState(0);
  const [maiorRolagem, setMaiorRolagem] = useState(0);

  useLayoutEffect(() => {
    setMaiorRolagem((atual) =>
      automatico && posicaoRolagem > 0 ? Math.max(atual, posicaoRolagem) : 0,
    );
  }, [automatico, posicaoRolagem]);

  useLayoutEffect(() => {
    if (!visualNovo || !detalhesRef.current) return;

    const detalhes = detalhesRef.current;

    const medir = () =>
      setAlturaDetalhes(detalhes.getBoundingClientRect().height);

    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(detalhes);

    return () => observador.disconnect();
  }, [visualNovo]);

  const deslocamento =
    automatico && posicaoRolagem > 0
      ? Math.max(maiorRolagem, posicaoRolagem)
      : 0;

  const distancia = Math.max(
    DISTANCIA_MINIMA_RECOLHIMENTO,
    2 * (alturaDetalhes + ESPACO_VERTICAL_RECOLHIVEL),
  );

  const progresso = Math.min(1, deslocamento / distancia);
  const abertura = 1 - progresso;

  // Ao chegar ao topo, anima a expansão do rodapé.
  const transicaoExpansao = posicaoRolagem === 0 ? "280ms ease-out" : "0ms";

  const podeConsultarModelos = ["superintendent", "analyst", "user"].includes(
    role,
  );

  const linkModelos = !condicao && podeConsultarModelos && (
    <Link
      component={RouterLink}
      to="/modelos"
      color="inherit"
      underline="hover"
      sx={{
        ...textoNovo,
        fontSize: visualNovo
          ? textoNovo.fontSize
          : ajustarTamanhoFonteAdmin("0.88rem"),
        display: "block",
        mt: visualNovo ? 0.1 : 0.5,
      }}
    >
      Modelos e Roteiros
    </Link>
  );

  const direitosAutorais = (
    <>
      © {new Date().getFullYear()} SRE - Superintendência Regional de Ensino.
      Todos os direitos reservados.
    </>
  );

  if (visualNovo) {
    return (
      <IdentidadeVisual
        component="footer"
        escuro={tema.palette.mode === "dark"}
        sx={{
          width: "calc(100% - 8px)",
          m: 0.5,
          borderRadius: "6px",
          height: "auto",
          minHeight: 0,
          flexShrink: 0,
          boxSizing: "border-box",
          boxShadow: "0 -2px 8px rgba(23,92,158,0.1)",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            width: "100%",
            height: "auto",
            boxSizing: "border-box",

            ...(automatico && {
              transition:
                `padding-top ${transicaoExpansao}, ` +
                `padding-bottom ${transicaoExpansao}`,
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
              },
            }),

            pt: automatico ? `${4 * abertura}px` : { xs: 1, md: 0.5 },

            pb: automatico ? `${2 * abertura}px` : { xs: 0.75, md: 0.25 },

            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            inert={automatico && progresso > 0 ? "" : undefined}
            aria-hidden={automatico && progresso > 0 ? true : undefined}
            sx={
              automatico
                ? {
                    height: `${alturaDetalhes * abertura}px`,
                    minHeight: 0,
                    overflow: "hidden",
                    transition: `height ${transicaoExpansao}`,
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }
                : undefined
            }
          >
            <Box
              ref={detalhesRef}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                columnGap: 0,
                rowGap: { xs: 1, md: 0 },
                alignItems: "start",
              }}
            >
              {/* ENDEREÇO */}
              <Box
                sx={{
                  ...linhaNova,
                  pl: { xs: 0, md: 2 },
                  pr: { xs: 0, md: 3 },
                  justifyContent: "flex-start",
                }}
              >
                <LocationOnRounded
                  aria-hidden="true"
                  sx={{
                    ...iconeNovo,
                    fontSize: 26,
                  }}
                />

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={textoNovo}>
                    R. Cel. Severiano, 351A - Tabajaras,
                  </Typography>

                  <Typography sx={textoNovo}>
                    Uberlândia - MG, CEP: 38400-228
                  </Typography>
                </Box>
              </Box>

              {/* LINKS ÚTEIS */}
              <Box
                sx={{
                  ...separadorNovo,
                  display: "flex",
                  justifyContent: {
                    xs: "flex-start",
                    md: "center",
                  },
                  px: { xs: 0, md: 3 },
                }}
              >
                <Box sx={linhaNova}>
                  <LinkRounded
                    aria-hidden="true"
                    sx={{
                      ...iconeNovo,
                      fontSize: 26,
                    }}
                  />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        ...textoNovo,
                        fontWeight: 700,
                        mb: 0,
                      }}
                    >
                      Links Úteis
                    </Typography>

                    <Link
                      href="https://sreuberlandia.educacao.mg.gov.br/"
                      color="inherit"
                      underline="hover"
                      sx={{
                        ...textoNovo,
                        display: "block",
                      }}
                    >
                      Informativo SRE
                    </Link>

                    {linkModelos}
                  </Box>
                </Box>
              </Box>

              {/* CONTATO */}
              <Box
                sx={{
                  ...separadorNovo,
                  display: "flex",
                  justifyContent: {
                    xs: "flex-start",
                    md: "flex-end",
                  },
                  pl: { xs: 0, md: 3 },
                  pr: { xs: 0, md: 2 },
                }}
              >
                <Box
                  sx={{
                    width: "fit-content",
                    maxWidth: "100%",
                    minWidth: 0,
                  }}
                >
                  <Box sx={linhaNova}>
                    <PhoneRounded aria-hidden="true" sx={iconeNovo} />

                    <Typography sx={textoNovo}>
                      (34) 3253-9234 / (34) 3253-9200 / (34) 3253-9220
                    </Typography>
                  </Box>

                  <Box sx={{ ...linhaNova, mt: 0.1 }}>
                    <EmailRounded aria-hidden="true" sx={iconeNovo} />

                    <Typography
                      sx={{
                        ...textoNovo,
                        minWidth: 0,
                        overflowWrap: "anywhere",
                      }}
                    >
                      sre.uberlandia.divaeprocessos@
                      {(rodapeAdmin ||
                        role === "superintendent" ||
                        role === "analyst" ||
                        role === "user") && <wbr />}
                      educacao.mg.gov.br
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* DIREITOS AUTORAIS */}
          <Typography
            align="center"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontSize: ajustarTamanhoFonteAdmin("0.8rem"),
              lineHeight: 1.25,
              mt: `${4 * abertura}px`,
              pt: "4px",
              pb: "4px",

              ...(automatico && {
                transition:
                  `margin-top ${transicaoExpansao}, ` +
                  `border-top-width ${transicaoExpansao}`,
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none",
                },
              }),

              borderTop: automatico
                ? `${abertura}px solid rgba(255,255,255,0.3)`
                : "1px solid var(--sear-borda, rgba(255,255,255,0.3))",
            }}
          >
            {direitosAutorais}
          </Typography>
        </Container>
      </IdentidadeVisual>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        color: "#ffffff",
        bgcolor: "var(--sear-acao-azul, #1871c9)",
        boxShadow: "0 -3px 10px rgba(0,0,0,0.1)",
        py: 1,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            gap: 4,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: ajustarTamanhoFonteAdmin("0.9rem"),
                fontWeight: 700,
              }}
            >
              SRE Uberlândia
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: ajustarTamanhoFonteAdmin("0.88rem"),
              }}
            >
              R. Cel. Severiano, 351A - Tabajaras, Uberlândia - MG
            </Typography>

            <Typography
              sx={{
                fontSize: ajustarTamanhoFonteAdmin("0.88rem"),
              }}
            >
              CEP: 38400-228
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: ajustarTamanhoFonteAdmin("0.95rem"),
                fontWeight: 700,
                mb: 1,
              }}
            >
              Links úteis
            </Typography>

            <Link
              href="https://sreuberlandia.educacao.mg.gov.br/"
              color="inherit"
              underline="hover"
              sx={{
                fontSize: ajustarTamanhoFonteAdmin("0.88rem"),
              }}
            >
              Informativo SRE
            </Link>

            {linkModelos}
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: ajustarTamanhoFonteAdmin("0.95rem"),
                fontWeight: 700,
                mb: 1,
              }}
            >
              Contato
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Phone sx={{ fontSize: 18, mt: 0.2 }} />

              <Typography
                sx={{
                  fontSize: ajustarTamanhoFonteAdmin("0.88rem"),
                }}
              >
                (34) 3253-9234 / (34) 3253-9200 / (34) 3253-9220
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mt: 1,
              }}
            >
              <Email sx={{ fontSize: 18, mt: 0.2 }} />

              <Typography
                sx={{
                  fontSize: ajustarTamanhoFonteAdmin("0.88rem"),
                  overflowWrap: "anywhere",
                }}
              >
                sre.uberlandia.divaeprocessos@educacao.mg.gov.br
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography
          align="center"
          sx={{
            mt: 2,
            fontSize: ajustarTamanhoFonteAdmin("0.82rem"),
          }}
        >
          {direitosAutorais}
        </Typography>
      </Container>
    </Box>
  );
}
